import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { UserPlus, Brain, Mail, Lock, AlertCircle } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { Card } from '../components/ui/Card'
import { Button } from '../components/ui/Button'
import { LanguageToggle } from '../components/ui/LanguageToggle'
import { ThemeToggle } from '../components/ui/ThemeToggle'
import { useLanguage } from '../contexts/LanguageContext'
import { sanitizeText, validateUserInput, checkRateLimit, logSecurityEvent } from '../utils/security'

export default function RegisterPage() {
  const { t } = useLanguage()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState(false)
  const [authUser, setAuthUser] = useState<any>(null)

  // Check for existing session
  useEffect(() => {
    const checkUser = async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (user) {
        setAuthUser(user)
      }
    }
    checkUser()

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_, session) => {
      setAuthUser(session?.user || null)
    })

    return () => subscription.unsubscribe()
  }, [])

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault()

    if (loading) return

    // Basic validation
    if (!email || !password || !confirmPassword) {
      setError(t('register.error.fillFields'))
      return
    }

    if (password !== confirmPassword) {
      setError(t('register.error.passwordMismatch'))
      return
    }

    if (password.length < 8) {
      setError(t('register.error.passwordLength'))
      return
    }

    // Security: Rate limiting
    if (!checkRateLimit(`register_${email}`, 3, 900000)) { // 3 attempts per 15 minutes
      setError(t('register.error.tooManyAttempts'))
      logSecurityEvent('Rate limit exceeded for registration', { email })
      return
    }

    // Security: Input validation
    const emailValidation = validateUserInput.email(email)
    if (!emailValidation.isValid) {
      setError(emailValidation.error || 'Invalid email')
      logSecurityEvent('Invalid email in registration', { email })
      return
    }

    setLoading(true)
    setError('')

    try {
      // For production, disable email confirmation temporarily
      const signUpOptions = window.location.hostname === 'localhost' ? {
        emailRedirectTo: `${window.location.origin}/beta`
      } : {
        emailRedirectTo: 'https://muay-thai.app/beta'
      }

      const { data, error: signUpError } = await supabase.auth.signUp({
        email: sanitizeText(email),
        password: password,
        options: signUpOptions
      })

      if (signUpError) {
        console.error('Registration error:', signUpError)

        if (signUpError.message.includes('already registered')) {
          setError(t('register.error.emailExists'))
        } else if (signUpError.message.includes('weak password')) {
          setError(t('register.error.weakPassword'))
        } else {
          setError(signUpError.message)
        }

        logSecurityEvent('Registration error', { error: signUpError.message, email })
        return
      }

      if (data.user) {
        logSecurityEvent('Successful registration', { email })

        // Direct redirect to dashboard instead of showing email confirmation
        window.location.href = '/dashboard'

        // Reset form
        setEmail('')
        setPassword('')
        setConfirmPassword('')
      }
    } catch (error) {
      console.error('Registration error:', error)
      setError(t('register.error.generic'))
      logSecurityEvent('Registration exception', { error })
    } finally {
      setLoading(false)
    }
  }

  const handleSignOut = async () => {
    await supabase.auth.signOut()
    setAuthUser(null)
  }

  // If user is logged in, show success message
  if (authUser) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-night-950 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full"
        >
          <Card className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 dark:bg-ocean-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Brain className="w-8 h-8 text-green-600 dark:text-ocean-400" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-slate-100 mb-2">{t('register.success.title')}</h2>
            <p className="text-gray-600 dark:text-slate-200 mb-6">
              {t('register.success.welcome')} {sanitizeText(authUser.email)}
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => window.location.href = '/dashboard'}
                className="w-full bg-purple-500 hover:bg-purple-600 dark:bg-ocean-500 dark:hover:bg-ocean-400"
              >
                🎯 Zum Training Dashboard
              </Button>
              <Button
                onClick={handleSignOut}
                className="w-full bg-gray-500 hover:bg-gray-600 dark:bg-night-700 dark:hover:bg-night-600"
              >
                {t('register.success.logout')}
              </Button>
            </div>
          </Card>
        </motion.div>
      </div>
    )
  }

  // Show registration success message
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-night-950 flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full"
        >
          <Card className="p-8 text-center">
            <div className="w-16 h-16 bg-blue-100 dark:bg-ocean-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-blue-600 dark:text-ocean-400" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-slate-100 mb-2">{t('register.emailSent.title')}</h2>
            <p className="text-gray-600 dark:text-slate-200 mb-6">
              {t('register.emailSent.message')}
            </p>
            <div className="space-y-3">
              <Button
                onClick={() => window.location.href = '/login'}
                className="w-full bg-purple-500 hover:bg-purple-600 dark:bg-ocean-500 dark:hover:bg-ocean-400"
              >
                {t('register.emailSent.toLogin')}
              </Button>
              <Button
                onClick={() => {
                  setSuccess(false)
                  setEmail('')
                  setPassword('')
                  setConfirmPassword('')
                }}
                className="w-full bg-gray-500 hover:bg-gray-600 dark:bg-night-700 dark:hover:bg-night-600"
              >
                {t('register.emailSent.backToRegister')}
              </Button>
            </div>
          </Card>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-night-950 flex items-center justify-center px-4">
      {/* Header Navigation */}
      <div className="absolute top-4 right-4 flex items-center space-x-3">
        <LanguageToggle />
        <ThemeToggle />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-md w-full"
      >
        <Card className="p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-purple-100 dark:bg-ocean-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <UserPlus className="w-8 h-8 text-purple-600 dark:text-ocean-400" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-slate-100 mb-2">
              {t('register.title')}
            </h1>
            <p className="text-gray-600 dark:text-slate-200">
              {t('register.subtitle')}
            </p>
          </div>

          {/* Registration Form */}
          <form onSubmit={handleRegister} className="space-y-6">
            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-lg p-4 flex items-center space-x-3"
              >
                <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
              </motion.div>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-slate-100 mb-2">
                <Mail className="w-4 h-4 inline mr-2" />
                {t('register.email')}
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-night-700 rounded-lg bg-white dark:bg-night-800 text-gray-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 dark:focus:ring-ocean-500 focus:border-transparent transition-colors"
                placeholder={t('register.emailPlaceholder')}
                required
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-slate-100 mb-2">
                <Lock className="w-4 h-4 inline mr-2" />
                {t('register.password')}
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-night-700 rounded-lg bg-white dark:bg-night-800 text-gray-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 dark:focus:ring-ocean-500 focus:border-transparent transition-colors"
                placeholder={t('register.passwordPlaceholder')}
                required
                disabled={loading}
                minLength={8}
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-slate-100 mb-2">
                <Lock className="w-4 h-4 inline mr-2" />
                {t('register.confirmPassword')}
              </label>
              <input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 dark:border-night-700 rounded-lg bg-white dark:bg-night-800 text-gray-900 dark:text-slate-100 focus:ring-2 focus:ring-purple-500 dark:focus:ring-ocean-500 focus:border-transparent transition-colors"
                placeholder={t('register.confirmPasswordPlaceholder')}
                required
                disabled={loading}
                minLength={8}
              />
            </div>

            <Button
              type="submit"
              disabled={loading || !email || !password || !confirmPassword}
              className="w-full bg-purple-500 hover:bg-purple-600 dark:bg-ocean-500 dark:hover:bg-ocean-400 text-white py-3 font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>{t('register.registering')}</span>
                </div>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  {t('register.submit')}
                </>
              )}
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-6 text-center text-sm text-gray-600 dark:text-slate-200">
            {t('register.hasAccount')}{' '}
            <a
              href="/login"
              className="text-purple-600 hover:text-purple-500 dark:text-ocean-400 dark:hover:text-ocean-300 font-medium transition-colors"
            >
              {t('register.loginLink')}
            </a>
          </div>
        </Card>
      </motion.div>
    </div>
  )
}